/**
 * 聊天消息面板（可复用）：消息流 + 输入框 + 3 秒轮询增量。
 *
 * 挂在两个地方：「我的队伍 → 队伍群聊 Tab」和「消息中心」右侧对话区。
 * 消息用 seq 游标拉增量（首次拉全量），自己发的消息本地即时上屏；
 * 打开面板即上报已读（markRead），未读角标随后端刷新。
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chatApi } from '../api/chat.js';
import type { ChatMessageDto } from '../api/types.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { Loading } from '../components/Loading.js';
import { qk } from '../lib/queryKeys.js';
import { formatRelative } from '../lib/format.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';

const POLL_MS = 3000;
const MAX_MESSAGE_LENGTH = 500;

interface ChatRoomProps {
  conversationId: string;
  /** 面板标题（会话名），显示在消息区顶部 */
  title: string;
  /** 副标题（如对方学校/队伍说明） */
  subtitle?: string;
}

export function ChatRoom({ conversationId, title, subtitle }: ChatRoomProps) {
  const me = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  /** 乐观上屏：发送成功前本地先显示 */
  const [optimistic, setOptimistic] = useState<ChatMessageDto[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const messagesQuery = useQuery({
    queryKey: qk.chatMessages(conversationId),
    queryFn: () => chatApi.listMessages(conversationId, 0),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });

  // 切换会话：清空草稿与乐观消息
  useEffect(() => {
    setDraft('');
    setOptimistic([]);
    setSendError(null);
  }, [conversationId]);

  // 打开即已读（一次/会话；轮询刷新未读角标）
  useEffect(() => {
    void chatApi.markRead(conversationId).then(() => {
      void queryClient.invalidateQueries({ queryKey: qk.chatUnread() });
      void queryClient.invalidateQueries({ queryKey: qk.conversations() });
    });
  }, [conversationId, queryClient]);

  // 新消息到达自动滚到底
  const messageCount = (messagesQuery.data?.messages.length ?? 0) + optimistic.length;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messageCount]);

  const send = useMutation({
    mutationFn: (body: string) => chatApi.sendMessage(conversationId, body),
    onSuccess: () => {
      setOptimistic([]);
      setSendError(null);
      void queryClient.invalidateQueries({ queryKey: qk.chatMessages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: qk.conversations() });
      void queryClient.invalidateQueries({ queryKey: qk.chatUnread() });
    },
    onError: (error) => {
      setOptimistic([]);
      setSendError(toUserMessage(error));
    },
  });

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const body = draft.trim();
    if (body.length === 0 || send.isPending) return;
    if (body.length > MAX_MESSAGE_LENGTH) {
      setSendError(`消息过长（上限 ${MAX_MESSAGE_LENGTH} 字）`);
      return;
    }
    setSendError(null);
    // 乐观上屏：senderId=me，发送失败时清掉并提示
    setOptimistic((prev) => [
      ...prev,
      {
        id: `optimistic-${prev.length}`,
        seq: Number.MAX_SAFE_INTEGER,
        conversationId,
        senderId: me?.id ?? '',
        senderName: me?.name ?? '我',
        body,
        createdAt: new Date().toISOString(),
      },
    ]);
    setDraft('');
    send.mutate(body);
  };

  const history = messagesQuery.data?.messages ?? [];
  // 合并历史与乐观消息（乐观在末尾）
  const visible = [...history, ...optimistic];

  return (
    <div className="chat-room">
      <div className="chat-room-head">
        <strong>{title}</strong>
        {subtitle !== undefined && subtitle.length > 0 ? <span className="muted small">{subtitle}</span> : null}
      </div>

      <div className="chat-scroll" role="log" aria-label="消息记录">
        {messagesQuery.isPending ? <Loading label="加载消息中" /> : null}
        {messagesQuery.isError ? (
          <p className="muted small">消息加载失败：{toUserMessage(messagesQuery.error)}</p>
        ) : null}
        {!messagesQuery.isPending && !messagesQuery.isError && visible.length === 0 ? (
          <EmptyState title="还没有消息" description="发第一条消息，和大家打个招呼吧" />
        ) : null}
        {visible.map((message) => {
          const mine = message.senderId === me?.id;
          return (
            <div key={message.id} className={mine ? 'chat-row mine' : 'chat-row'}>
              <div className="chat-bubble-meta">
                <span className="chat-sender">{mine ? '我' : message.senderName}</span>
                <span className="muted small">{formatRelative(message.createdAt)}</span>
              </div>
              <div className={mine ? 'chat-bubble mine' : 'chat-bubble'}>{message.body}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form className="chat-input-row" onSubmit={onSubmit}>
        <input
          className="input chat-input"
          value={draft}
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder="输入消息，回车发送"
          onChange={(event) => setDraft(event.target.value)}
          aria-label="消息内容"
        />
        <Button type="submit" loading={send.isPending} disabled={draft.trim().length === 0}>
          发送
        </Button>
      </form>
      <div className="chat-foot">
        {sendError !== null ? <span className="field-error small">{sendError}</span> : null}
        <span className="muted small">
          {draft.length}/{MAX_MESSAGE_LENGTH}
        </span>
      </div>
    </div>
  );
}

/** 轻量提示：mutation 错误由 ChatRoom 内部展示，这里留给调用方 toast 用 */
export function notifyChatError(error: unknown): void {
  toast.error('操作失败', toUserMessage(error));
}
